'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var i18n = require('i18next');
var LanguageDetector = require('i18next-browser-languagedetector');
var React = require('react');
var reactI18next = require('react-i18next');
var constants = require('./constants.cjs');
var dates = require('./dates.cjs');
var languages = require('./languages.cjs');

function _interopDefaultCompat (e) { return e && typeof e === 'object' && 'default' in e ? e : { default: e }; }

var i18n__default = /*#__PURE__*/_interopDefaultCompat(i18n);
var LanguageDetector__default = /*#__PURE__*/_interopDefaultCompat(LanguageDetector);
var React__default = /*#__PURE__*/_interopDefaultCompat(React);

"use strict";
let tFunc;
let transComponent;
const VALID_LANGUAGES = [
  ...languages.LANGUAGES,
  {
    name: "Pseudo",
    code: constants.PSEUDO_LOCALE
  }
];
function initTFuncAndTransComponent({ id, ns } = {}) {
  if (id) {
    tFunc = getI18nInstance().getFixedT(null, id);
    transComponent = (props) => /* @__PURE__ */ React__default.default.createElement(reactI18next.Trans, { shouldUnescape: true, ns: id, ...props });
    return;
  }
  tFunc = getI18nInstance().t;
  transComponent = (props) => /* @__PURE__ */ React__default.default.createElement(reactI18next.Trans, { shouldUnescape: true, ns, ...props });
}
async function loadNamespacedResources(namespace, language, loaders) {
  if (!(loaders == null ? void 0 : loaders.length)) {
    return;
  }
  const resolvedLanguage = language === constants.PSEUDO_LOCALE ? constants.DEFAULT_LANGUAGE : language;
  return Promise.all(
    loaders.map(async (loader) => {
      try {
        const resources = await loader(resolvedLanguage);
        addResourceBundle(resolvedLanguage, namespace, resources);
      } catch (error) {
        console.error(`Error loading resources for namespace ${namespace} and language: ${resolvedLanguage}`, error);
      }
    })
  );
}
function initDefaultI18nInstance() {
  var _a;
  if (((_a = getI18nInstance().options) == null ? void 0 : _a.resources) && typeof getI18nInstance().options.resources === "object") {
    return;
  }
  const initPromise = getI18nInstance().use(reactI18next.initReactI18next).init({
    resources: {},
    returnEmptyString: false,
    lng: constants.DEFAULT_LANGUAGE
    // this should be the locale of the phrases in our source JSX
  });
  initTFuncAndTransComponent();
  return initPromise;
}
function initDefaultReactI18nInstance() {
  var _a, _b;
  if ((_b = (_a = reactI18next.getI18n()) == null ? void 0 : _a.options) == null ? void 0 : _b.react) {
    return;
  }
  const options = {};
  reactI18next.setDefaults(options);
  reactI18next.setI18n(getI18nInstance());
}
async function initPluginTranslations(id, loaders) {
  await initDefaultI18nInstance();
  initDefaultReactI18nInstance();
  const language = getResolvedLanguage();
  initTFuncAndTransComponent({ id });
  await loadNamespacedResources(id, language, loaders);
  return { language };
}
function getI18nInstance() {
  const instance = i18n__default.default;
  if (instance && instance.default) {
    return instance.default;
  }
  return instance;
}
async function initTranslations({
  ns,
  language = constants.DEFAULT_LANGUAGE,
  module
}) {
  var _a, _b;
  const options = {
    // We don't bundle any translations, we load them async
    partialBundledLanguages: true,
    resources: {},
    // If translations are empty strings (no translation), fall back to the default value in source code
    returnEmptyString: false,
    // Required to ensure that `resolvedLanguage` is set property when an invalid language is passed (such as through 'detect')
    supportedLngs: VALID_LANGUAGES.map((lang) => lang.code),
    fallbackLng: constants.DEFAULT_LANGUAGE,
    ns,
    postProcess: [
      // Add pseudo processing even if we aren't necessarily going to use it
      constants.PSEUDO_LOCALE
    ]
  };
  if (language === "detect") {
    getI18nInstance().use(LanguageDetector__default.default);
    const detection = { order: ["navigator"], caches: [] };
    options.detection = detection;
  } else {
    options.lng = (_b = (_a = VALID_LANGUAGES.find((lang) => lang.code === language)) == null ? void 0 : _a.code) != null ? _b : void 0;
  }
  if (module) {
    getI18nInstance().use(module).use(reactI18next.initReactI18next);
  } else {
    getI18nInstance().use(reactI18next.initReactI18next);
  }
  if (language === constants.PSEUDO_LOCALE) {
    const { default: Pseudo } = await import('i18next-pseudo');
    getI18nInstance().use(
      new Pseudo({
        languageToPseudo: constants.PSEUDO_LOCALE,
        enabled: true,
        wrapped: true
      })
    );
  }
  await getI18nInstance().init(options);
  initTFuncAndTransComponent({ ns });
  return {
    language: getResolvedLanguage()
  };
}
function getLanguage() {
  var _a;
  return ((_a = getI18nInstance()) == null ? void 0 : _a.language) || constants.DEFAULT_LANGUAGE;
}
function getResolvedLanguage() {
  var _a;
  return ((_a = getI18nInstance()) == null ? void 0 : _a.resolvedLanguage) || constants.DEFAULT_LANGUAGE;
}
function getNamespaces() {
  var _a;
  return (_a = getI18nInstance()) == null ? void 0 : _a.options.ns;
}
async function changeLanguage(language) {
  var _a, _b;
  const validLanguage = (_b = (_a = VALID_LANGUAGES.find((lang) => lang.code === language)) == null ? void 0 : _a.code) != null ? _b : constants.DEFAULT_LANGUAGE;
  await getI18nInstance().changeLanguage(validLanguage);
}
async function initializeI18n({ language, ns, module }, regionalFormat) {
  dates.initRegionalFormat(regionalFormat);
  return initTranslations({ language, ns, module });
}
function addResourceBundle(language, namespace, resources) {
  getI18nInstance().addResourceBundle(language, namespace, resources, true, false);
}
const t = (id, defaultMessage, values) => {
  initDefaultI18nInstance();
  if (!tFunc) {
    if (process.env.NODE_ENV !== "test") {
      console.warn(
        "t() was called before i18n was initialized. This is probably caused by calling t() in the root module scope, instead of lazily on render"
      );
    }
    if (process.env.NODE_ENV === "development") {
      throw new Error("t() was called before i18n was initialized");
    }
    tFunc = getI18nInstance().t;
  }
  return tFunc(id, defaultMessage, values);
};
function Trans(props) {
  initDefaultI18nInstance();
  const Component = transComponent != null ? transComponent : reactI18next.Trans;
  return /* @__PURE__ */ React__default.default.createElement(Component, { shouldUnescape: true, ...props });
}

exports.Trans = Trans;
exports.addResourceBundle = addResourceBundle;
exports.changeLanguage = changeLanguage;
exports.getI18nInstance = getI18nInstance;
exports.getLanguage = getLanguage;
exports.getNamespaces = getNamespaces;
exports.getResolvedLanguage = getResolvedLanguage;
exports.initDefaultI18nInstance = initDefaultI18nInstance;
exports.initDefaultReactI18nInstance = initDefaultReactI18nInstance;
exports.initPluginTranslations = initPluginTranslations;
exports.initializeI18n = initializeI18n;
exports.loadNamespacedResources = loadNamespacedResources;
exports.t = t;
//# sourceMappingURL=i18n.cjs.map
